import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ChevronDown, Clock } from "lucide-react";
import { getArticle, ARTICLES } from "../data/articles";
import { useMedia } from "../hooks/useMedia";

function FaqRow({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: "1px solid var(--line)" }}>
      <button onClick={() => setOpen(!open)} className="w-full py-6 flex items-center justify-between text-left group" data-cursor="hover" data-testid={`article-faq-${i}`}>
        <span className="font-display text-lg md:text-xl group-hover:text-[var(--gold-2)] transition-colors" style={{ letterSpacing: "-0.015em" }}>{q}</span>
        <ChevronDown size={16} className="transition-transform duration-500 shrink-0 ml-6" style={{ color: "var(--gold)", transform: open ? "rotate(180deg)" : "rotate(0)" }} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="pb-6 font-body max-w-3xl overflow-hidden"
            style={{ color: "var(--text-2)", lineHeight: 1.8, fontSize: 15 }}
          >
            {a}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function LearningArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);
  const { url } = useMedia();

  // Inject Article JSON-LD + set document title / description for SEO
  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    document.title = `${article.title} · Bespoke Learning Center`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", article.excerpt);

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.article = article.slug;
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      articleSection: article.category,
      wordCount: article.body.reduce((n, b) => n + (b.text || "").split(/\s+/).length, 0),
      author: { "@type": "Organization", "name": "Bespoke Exterior Solutions" },
      publisher: { "@type": "Organization", "name": "Bespoke Exterior Solutions", "url": window.location.origin },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${window.location.origin}/learn/${article.slug}` },
      image: url(article.heroId) || undefined,
    });
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
      script.remove();
    };
  }, [article, url]);

  if (!article) return <Navigate to="/learn" replace />;

  const related = (() => {
    const sameCat = ARTICLES.filter((a) => a.slug !== article.slug && a.category === article.category);
    if (sameCat.length >= 2) return sameCat.slice(0, 2);
    // Fallback: fill with any other articles so the "More from…" grid always shows two tiles
    const others = ARTICLES.filter((a) => a.slug !== article.slug && a.category !== article.category);
    return [...sameCat, ...others].slice(0, 2);
  })();

  return (
    <div className="min-h-screen" data-testid="learning-article-page">
      {/* Hero */}
      <section className="relative w-full flex items-end overflow-hidden" style={{ minHeight: "70vh", background: "var(--bg-2)" }}>
        {url(article.heroId) && (
          <motion.img
            src={url(article.heroId)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ scale: 1.12, opacity: 0 }}
            animate={{ scale: 1.02, opacity: 1 }}
            transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
            draggable={false}
          />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0.92) 100%)" }} />

        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-14 pb-16 md:pb-24 pt-40">
          <Link to="/learn" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.44em]" style={{ color: "var(--gold)" }} data-testid="article-back-link">
            <ArrowLeft size={12} /> Learning Center
          </Link>
          <div className="mt-8 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-2)" }}>
            <span className="pill">{article.category}</span>
            <span className="flex items-center gap-2"><Clock size={12} /> {article.readTime}</span>
          </div>
          <h1 className="mt-6 font-display leading-[0.95]" style={{ fontSize: "clamp(2.4rem, 5.5vw, 5rem)", letterSpacing: "-0.03em" }}>{article.title}</h1>
          <p className="mt-6 max-w-3xl font-body text-lg" style={{ color: "var(--text-2)", lineHeight: 1.7 }}>{article.excerpt}</p>
        </div>
      </section>

      {/* Body */}
      <section className="py-24 md:py-32" style={{ background: "var(--bg)" }}>
        <div className="max-w-[900px] mx-auto px-6 sm:px-10 lg:px-14">
          <article className="prose-editorial">
            {article.body.map((block, i) => {
              if (block.type === "h2") {
                return (
                  <h2 key={i} className="font-display text-3xl md:text-4xl mt-16 mb-6 first:mt-0" style={{ letterSpacing: "-0.025em", lineHeight: 1.15 }}>
                    {block.text}
                  </h2>
                );
              }
              return (
                <p
                  key={i}
                  className="mt-6 font-body first:mt-0"
                  style={{ color: "var(--text-2)", lineHeight: 1.9, fontSize: 18 }}
                  dangerouslySetInnerHTML={{ __html: block.text }}
                />
              );
            })}
          </article>

          {/* FAQ */}
          {article.faq && article.faq.length > 0 && (
            <div className="mt-24">
              <div className="overline mb-8">Frequently Asked</div>
              {article.faq.map((f, i) => <FaqRow key={f.q} q={f.q} a={f.a} i={i} />)}
              <div style={{ borderTop: "1px solid var(--line)" }} />
            </div>
          )}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-24 md:py-28" style={{ background: "var(--bg-2)" }}>
          <div className="max-w-[1300px] mx-auto px-6 sm:px-10 lg:px-14">
            <div className="overline mb-10">More reading</div>
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/learn/${a.slug}`}
                  className="glass-card overflow-hidden group flex flex-col"
                  data-testid={`related-article-${a.slug}`}
                  data-cursor="hover"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    {url(a.heroId) && <img src={url(a.heroId)} alt="" className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" draggable={false} />}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 45%, rgba(0,0,0,0.85) 100%)" }} />
                  </div>
                  <div className="p-8">
                    <h3 className="font-display text-2xl leading-[1.15]" style={{ letterSpacing: "-0.02em" }}>{a.title}</h3>
                    <p className="mt-4 font-body" style={{ color: "var(--text-2)", lineHeight: 1.65, fontSize: 15 }}>{a.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-28 md:py-36 text-center" style={{ background: "var(--bg)" }}>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl mx-auto px-6" style={{ letterSpacing: "-0.035em" }}>
          Bring the <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>standard</span> to your property.
        </h2>
        <div className="mt-12 flex flex-wrap justify-center gap-5">
          <Link to="/quote" className="btn-glow" data-cursor="hover" data-testid="article-cta-quote">
            Request a Quote <ArrowUpRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
}
